import { AuthService, MFAService } from '@yubi/yb-module-auth';
import humps from 'humps';
import Cookie from 'js-cookie';
import debounce from 'lodash.debounce';
import { toastr } from 'react-redux-toastr';

import {
    CREDAVENUE_DOMAIN,
    YUBI_DOMAIN,
    NETWORK_CALL_TIMEOUT,
    ERR_LOGIN_REQUIRED,
} from 'constants/AppConstants';
import Logger from 'utils/Logger';

const parseDomain = () => {
    const [subDomain, domain, type] = window.location.host?.split('.');
    return { subDomain, domain, type };
};

const getCurrentDomain = () => {
    const { domain } = parseDomain();
    return domain;
};

const arrayBufferToHexString = (buffer: any) => {
    const byteArray = new Uint8Array(buffer);
    const hexString = Array.from(byteArray, (byte) => {
        return `0${byte.toString(16)}`.slice(-2);
    }).join('');
    return hexString;
};

const hashEmail = async (email: any) => {
    const encoder = new TextEncoder();
    const data = encoder.encode(email);
    const hash = await crypto.subtle.digest('SHA-256', data);
    const hashedEmail = arrayBufferToHexString(hash);
    return hashedEmail;
};

export async function checkStatus({ response, apiMethod }: any) {
    const { status, statusText, url } = response;
    if (status >= 200 && status < 300) return response;
    const error = new Error(statusText);
    const requestId = response?.headers?.get('x-request-id');
    error.response = response;
    const apiError = new Error('API error');

    const { email = '' } = AuthService.getActiveUser() || {};
    const { group = '', role = '', sub_group = '' } = AuthService.getActiveUserGroup() || {};

    const hashedEmail = await hashEmail(email);

    // Logger.logException(
    //     apiError,
    //     {
    //         level: 'debug',
    //         tags: humps.camelizeKeys({
    //             ...(AuthService.getActiveUserGroup() || {}),
    //             ...(AuthService.getActiveUser() || {}),
    //             status,
    //             apiUrl: url,
    //             requestId,
    //         }),
    //         contexts: {
    //             error,
    //             payload: {
    //                 statusText,
    //             },
    //         },
    //     },
    //     {
    //         level: 'debug',
    //         role,
    //         group,
    //         subGroup: sub_group,
    //         entityId: AuthService.getActiveEntityId() || '',
    //         userId: AuthService.getActiveUserId() || '',
    //         hashedEmail,
    //         apiMethod,
    //         status,
    //         apiUrl: url,
    //         requestId,
    //     },
    // );
    throw error;
}

const ipAddressBlocked = () => {
    if (window.sessionStorage.getItem('ip-address-blocked'))
        return Promise.resolve('Your ip address is blocked');
    window.sessionStorage.setItem('ip-address-blocked', 'true');
    window.location.href = '/ip-address-blocked';
    return Promise.resolve('Your ip address is blocked');
};

export const logoutUser = () => {
    return postJSON(`${process.env.REACT_APP_AUTH_API_HOST}/auth/logout`).catch(() => {
        // do nothing
    });
};

export const getEnv = (endPoint: any, resolveDomain = true) => {
    if (endPoint?.startsWith('/')) return endPoint;
    if (resolveDomain && getCurrentDomain() === YUBI_DOMAIN) {
        return endPoint?.replace(CREDAVENUE_DOMAIN, YUBI_DOMAIN);
    }
    if (resolveDomain && getCurrentDomain() === CREDAVENUE_DOMAIN) {
        return endPoint?.replace(YUBI_DOMAIN, CREDAVENUE_DOMAIN);
    }
    return endPoint;
};

const logoutUserWithError = debounce(() => {
    logoutUser().then(() => {
        toastr.error('Authentication failed', 'Please re-login');
        setTimeout(() => {
            AuthService.logout();
            window.location.href = `https://${process.env.REACT_APP_AUTH_DOMAIN_ADDRESS}/v2/logout?client_id=${process.env.REACT_APP_AUTH_CLIENT_ID}&returnTo=${window.location.origin}`;
        }, 2000);
    });
}, 100);

const redirectToOtpScreen = () => {
    Cookie.remove('mfa_token', {
        domain: `.${process.env.REACT_APP_HOST}`,
        path: '/',
    });
    Cookie.remove('mfa_token', {
        domain: `.${process.env.REACT_APP_NEW_HOST}`,
        path: '/',
    });
    window.location.href = window.location.origin;
};

export function handleErrorV2(dispatch: any, ex: any, defaultMsg = 'Please try again...') {
    const { error = null } = ex || {};
    const status = ex && ex.response && ex.response.status;
    if (status === undefined) {
        defaultMsg = 'cancelled';
    }
    if (error === ERR_LOGIN_REQUIRED) {
        logoutUserWithError();
    }
    if (typeof dispatch === 'function') {
        if (status === 401) {
            return parseJSON(ex.response).then((d) => {
                if (d.error && d.error.message === 'Invalid MFA token') {
                    redirectToOtpScreen();
                    return Promise.resolve('Invalid MFA Token');
                }
                logoutUserWithError();
                return Promise.resolve('Please re-login to try again');
            });
        }
        if (status === 403) {
            window.location.href = '/forbidden';
            return Promise.resolve("You don't seem to have access to this part of the app");
        }
        if (status === 432) {
            return ipAddressBlocked();
        }
        if (status === 499) {
            window.location.href = '/no-access';
            return Promise.resolve("You don't have access to this page");
        }
    }
    return handleError(ex, defaultMsg);
}

export const handleSWRError = (err: any, key: any, config: any) => {
    const { statusText, status } = err?.response || {};
    if (statusText === ERR_LOGIN_REQUIRED) {
        logoutUserWithError();
    }
    if (status === 401) {
        if (statusText === 'Invalid MFA token') {
            redirectToOtpScreen();
            return Promise.resolve('Invalid MFA Token');
        }
        logoutUserWithError();
        return Promise.resolve('Please re-login to try again');
    }
    if (status === 403) {
        window.location.href = '/forbidden';
        return Promise.resolve("You don't seem to have access to this part of the app");
    }
    if (status === 432) {
        return ipAddressBlocked();
    }
    if (status === 499) {
        window.location.href = '/no-access';
        return Promise.resolve("You don't have access to this page");
    }
};

export const handleGenericError = (...rest: any) => {
    console.log(rest);
};

export function handleError(ex: any, defaultMsg = 'Please try again...') {
    const { statusText, status, data: respData = {} } = ex?.response || {};
    let defaultMessage = defaultMsg;
    if (status === undefined) {
        defaultMessage = 'cancelled';
    }
    if (statusText === ERR_LOGIN_REQUIRED) {
        logoutUserWithError();
        return null;
    }
    if (status === 401) {
        if (statusText === 'Invalid MFA token') {
            redirectToOtpScreen();
            return Promise.resolve('Invalid MFA Token');
        }
        logoutUserWithError();
        return Promise.resolve('Please re-login to try again');
    }
    if (status === 403) {
        window.location.href = '/forbidden';
        return Promise.resolve("You don't seem to have access to this part of the app");
    }
    if (status === 499) {
        window.location.href = '/no-access';
        return Promise.resolve("You don't have access to this page");
    }
    if (status === 432) {
        return ipAddressBlocked();
    }
    return new Promise((resolve) => {
        const axiosError = respData?.error?.message || '';
        if (!ex.response) {
            resolve(defaultMessage);
        }
        if (axiosError) {
            resolve(axiosError);
        }
        ex.response
            .json()
            .then((data: any) => {
                const { message } = data.error || data.message || defaultMessage;
                if (typeof message === 'string') {
                    resolve(message);
                }
                if (typeof data?.message === 'string') resolve(data?.message);
                resolve(defaultMessage);
            })
            .catch((e: any) => {
                Logger.logException(e);
            });
    });
}

export function parseJSON(response: any) {
    return response && response.json();
}

export function parseBlob(response: any) {
    return response && response.blob();
}

export function setPostHeaders(body: any, customHeader = {}, isJsonBody = true) {
    const accessToken = AuthService.getAccessToken();
    const mfaToken = MFAService.getMFAToken();
    const roleHeaders = getGroupHeaders();
    const h = { ...customHeader };
    h.Accept = 'application/json';
    h['Content-Type'] = 'application/json';
    if (accessToken) {
        h.Authorization = `Bearer ${accessToken}`;
        h['Mfa-Token'] = mfaToken;
    }
    return {
        method: 'POST',
        headers: { ...h, ...roleHeaders },
        body: isJsonBody ? JSON.stringify(body) : body,
    };
}

export function setGetHeaders(_: any, customHeader = {}) {
    const accessToken = AuthService.getAccessToken();
    const mfaToken = MFAService.getMFAToken();
    const roleHeaders = getGroupHeaders();
    const h = { ...customHeader };
    h.Accept = 'application/json';
    h['Content-Type'] = 'application/json';
    if (accessToken) {
        h.Authorization = `Bearer ${accessToken}`;
        h['Mfa-Token'] = mfaToken;
    }
    return {
        method: 'GET',
        headers: { ...h, ...roleHeaders },
    };
}

export function setPutHeaders(body: any, isJsonBody = true) {
    const accessToken = AuthService.getAccessToken();
    const mfaToken = MFAService.getMFAToken();
    const roleHeaders = getGroupHeaders();
    const h = {};
    h.Accept = 'application/json';
    h['Content-Type'] = 'application/json';
    if (accessToken) {
        h.Authorization = `Bearer ${accessToken}`;
        h['Mfa-Token'] = mfaToken;
    }
    return {
        method: 'PUT',
        headers: { ...h, ...roleHeaders },
        body: isJsonBody ? JSON.stringify(body) : body,
    };
}

export function setPatchHeaders(body: any, isJsonBody = true) {
    const accessToken = AuthService.getAccessToken();
    const mfaToken = MFAService.getMFAToken();
    const roleHeaders = getGroupHeaders();
    const h = {};
    h.Accept = 'application/json';
    h['Content-Type'] = 'application/json';
    if (accessToken) {
        h.Authorization = `Bearer ${accessToken}`;
        h['Mfa-Token'] = mfaToken;
    }
    return {
        method: 'PATCH',
        headers: { ...h, ...roleHeaders },
        body: isJsonBody ? JSON.stringify(body) : body,
    };
}

export function setDeleteHeaders(body: any, isJsonBody = true) {
    const accessToken = AuthService.getAccessToken();
    const mfaToken = MFAService.getMFAToken();
    const roleHeaders = getGroupHeaders();
    const h = {};
    h.Accept = 'application/json';
    h['Content-Type'] = 'application/json';
    if (accessToken) {
        h.Authorization = `Bearer ${accessToken}`;
        h['Mfa-Token'] = mfaToken;
    }
    return {
        method: 'DELETE',
        headers: { ...h, ...roleHeaders },
        body: isJsonBody ? JSON.stringify(body) : body,
    };
}

export function poll(fn: any, timeout: any, interval = 100) {
    const endTime = Number(new Date()) + (timeout || 2000);

    const checkCondition = (resolve: any, reject: any) => {
        // If the condition is met, we're done!
        const result = fn();

        result
            .then((r: any) => {
                if (r instanceof Error) {
                    reject(r);
                } else if (r) {
                    resolve(r);
                } else if (Number(new Date()) < endTime) {
                    // If the condition isn't met but the timeout hasn't elapsed, go again
                    setTimeout(checkCondition, interval, resolve, reject);
                } else {
                    // Didn't match and too much time, reject!
                    reject(new Error('timeout'));
                }
            })
            .catch((e) => {
                reject(e);
            });
    };

    return new Promise(checkCondition);
}

const checkTokenAndFetch = ({ url, headers, body = null, customHeader = {}, signal = null }: any) =>
    new Promise((resolve, reject) => {
        const apURL = getEnv(url);
        if (AuthService.isAccessTokenExpiring()) {
            AuthService.renewToken()
                .then(() => {
                    resolve(
                        fetch(apURL, {
                            ...headers(body, customHeader),
                            signal,
                            timeout: NETWORK_CALL_TIMEOUT,
                        })
                    );
                })
                .catch((err) => {
                    reject(err);
                });
        } else {
            try {
                resolve(
                    fetch(apURL, {
                        ...headers(body, customHeader),
                        signal,
                        timeout: NETWORK_CALL_TIMEOUT,
                    })
                );
            } catch (e) {
                reject(e);
            }
        }
    });

export const getJSONCopy = (url, signal?: any, body?: any, additionalHeaders?: any) => {
    return checkTokenAndFetch(url, requestPayload('GET', body, additionalHeaders), signal)
        .then(checkStatus)
        .then(parseJSON)
        .catch((ex) => {
            const error = { ...ex };
            if (!signal) throw error;
        });
};

export const getJSON = (url: any, signal?: any) => {
    return checkTokenAndFetch({
        url,
        headers: setGetHeaders,
        body: null,
        signal,
    })
        .then((res) => {
            return checkStatus({ response: res, apiMethod: 'get' });
        })
        .then(parseJSON);
};

export const getFileJSON = (url: any, signal: any) => {
    return checkTokenAndFetch({
        url,
        headers: setGetHeaders,
        body: null,
        signal,
    }).then((res) => {
        return checkStatus({ response: res, apiMethod: 'get' });
    });
};

export const putJSON = (url: any, body: any) => {
    return checkTokenAndFetch({ url, headers: setPutHeaders, body })
        .then((res) => {
            return checkStatus({ response: res, apiMethod: 'put' });
        })
        .then(parseJSON);
};

export const patchJSON = (url: any, body: any) => {
    return checkTokenAndFetch({ url, headers: setPatchHeaders, body })
        .then((res) => {
            return checkStatus({ response: res, apiMethod: 'patch' });
        })
        .then(parseJSON);
};

export const deleteJSON = (url: any, body = {}) => {
    return checkTokenAndFetch({ url, headers: setDeleteHeaders, body })
        .then((res) => {
            return checkStatus({ response: res, apiMethod: 'delete' });
        })
        .then(parseJSON);
};

export const postJSON = (url: any, body: any, customHeader = {}) => {
    return checkTokenAndFetch({
        url,
        headers: setPostHeaders,
        body,
        customHeader,
    })
        .then((res) => {
            return checkStatus({ response: res, apiMethod: 'post' });
        })
        .then(parseJSON);
};

export const postJSONWithoutParse = (url: any, body: any, customHeader = {}) => {
    return checkTokenAndFetch({
        url,
        headers: setPostHeaders,
        body,
        customHeader,
    }).then((res) => {
        return checkStatus({ response: res, apiMethod: 'post' });
    });
};

export const getJSONAndDownload = (url: any, body: any, signal: any) => {
    return checkTokenAndFetch({ url, headers: setGetHeaders, body, signal })
        .then((res) => {
            return checkStatus({ response: res, apiMethod: 'get' });
        })
        .then(parseBlob);
};

export const postJSONAndDownload = (url: any, body: any) => {
    return checkTokenAndFetch({ url, headers: setPostHeaders, body })
        .then((res) => {
            return checkStatus({ response: res, apiMethod: 'post' });
        })
        .then(parseBlob);
};

export const getAuthHeaders = () => {
    const accessToken = AuthService.getAccessToken();
    const h = {};
    if (accessToken) {
        h.Authorization = `Bearer ${accessToken}`;
    }
    return h;
};

export function getGroupHeaders() {
    const groupHeader = {};
    const getActiveGroup = AuthService.getActiveUserGroup();
    const subgroup = getActiveGroup?.sub_group;

    if (subgroup) {
        groupHeader['Current-Sub-group'] = subgroup;
    }
    if (getActiveGroup) {
        groupHeader['Current-Entity-Id'] = getActiveGroup.entity_id;
        groupHeader['Current-Group'] = humps.decamelize(getActiveGroup.group, {
            separator: '_',
        });
    }
    return groupHeader;
}

export const downloadFile = (data: any, filename: any, mime = '') => {
    const blob = new Blob([data], {
        type: mime || 'application/octet-stream',
    });
    const blobURL = window.URL.createObjectURL(blob);
    const tempLink = document.createElement('a');
    tempLink.style.display = 'none';
    tempLink.href = blobURL;
    tempLink.setAttribute('download', filename);

    if (typeof tempLink.download === 'undefined') {
        tempLink.setAttribute('target', '_blank');
    }
    document.body.appendChild(tempLink);
    tempLink.click();
    document.body.removeChild(tempLink);
    setTimeout(() => {
        window.URL.revokeObjectURL(blobURL);
    }, 100);
};

export const getJSONForBillDiscounting = (url: any, signal: any = null) =>
    checkTokenAndFetch({ url, headers: setGetHeadersForBillDiscounting, signal })
        .then(checkStatus)
        .then(parseJSON);

export const setGetHeadersForBillDiscounting = () => {
    const accessToken = AuthService.getAccessToken();
    const mfaToken = MFAService.getMFAToken();
    const roleHeaders = getGroupHeadersForBillDiscounting();
    const h: any = {};
    h.Accept = 'application/json';
    h['Content-Type'] = 'application/json';
    if (accessToken) {
        h.Authorization = `Bearer ${accessToken}`;
        h['Mfa-Token'] = mfaToken;
    }
    return {
        method: 'GET',
        headers: { ...h, ...roleHeaders },
    };
};

export function getGroupHeadersForBillDiscounting() {
    const groupHeader: any = {};
    const getActiveGroup = getActiveUserGroup();
    if (getActiveGroup) {
        const group = humps.decamelize(getActiveGroup.group, {
            separator: '_',
        });
        const subGroup =
            getActiveGroup.sub_group &&
            humps.decamelize(getActiveGroup.sub_group, {
                separator: '_',
            });
        groupHeader['Current-Entity-Id'] = getActiveGroup.entity_id;

        if (group === 'customer') {
            groupHeader['Current-Group'] = subGroup;
        } else {
            groupHeader['Current-Group'] = group;
        }
    }
    return groupHeader;
}

const getActiveUserGroup = () => {
    const activeUserGroup = Cookie.get('activeUserGroup');
    return activeUserGroup ? JSON.parse(activeUserGroup) : null;
};
